import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { taskNavRef } from '../utils/taskNav';

const STATUS_LABELS = {
  IN_PROGRESS: 'В работе',
  DONE: 'Завершён',
};

const FILTER_TABS = [
  { id: 'IN_PROGRESS', label: 'В работе' },
  { id: 'DONE', label: 'Завершён' },
  { id: 'ALL', label: 'Все' },
];

export default function TaskListPage() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [finishingId, setFinishingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('IN_PROGRESS');

  useEffect(() => {
    fetchLists();
  }, []);

  async function fetchLists() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tasks/lists/');
      setLists(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setError('Не удалось загрузить списки задач');
    } finally {
      setLoading(false);
    }
  }

  const visibleLists = useMemo(() => {
    const arr = Array.isArray(lists) ? lists : [];
    const filtered = statusFilter === 'ALL'
      ? arr
      : arr.filter((lst) => lst?.student_status === statusFilter);
    const sorted = [...filtered].sort((a, b) => {
      const ta = a?.issued_at ? Date.parse(a.issued_at) : NaN;
      const tb = b?.issued_at ? Date.parse(b.issued_at) : NaN;
      const aOk = Number.isFinite(ta);
      const bOk = Number.isFinite(tb);
      if (aOk && bOk) return tb - ta; // desc (newest first)
      if (aOk && !bOk) return -1;
      if (!aOk && bOk) return 1;
      return 0;
    });
    return sorted;
  }, [lists, statusFilter]);

  async function toggleFinish(lst) {
    if (!lst?.id) return;
    setFinishingId(lst.id);
    try {
      const action = lst.student_status === 'DONE' ? 'reopen' : 'finish';
      await api.post(`/tasks/lists/${lst.id}/finish/`, { action });
      await fetchLists();
    } catch {
      setError('Не удалось изменить статус списка.');
    } finally {
      setFinishingId(null);
    }
  }

  return (
    <div className="task-list-page">
      <div className="page-header">
        <h1>Мои списки задач</h1>
        <p className="page-subtitle">
          Домашние задания от преподавателя и ваши собственные списки для решения из каталога.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <p className="text-secondary">Загрузка...</p>
      ) : visibleLists.length === 0 ? (
        <p className="text-secondary">Списков пока нет.</p>
      ) : (
        <>
          <div className="task-list-filters">
            <div className="task-list-filter-tabs" role="tablist" aria-label="Фильтр статуса списков">
              {FILTER_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`task-list-filter-tab ${statusFilter === t.id ? 'active' : ''}`}
                  onClick={() => setStatusFilter(t.id)}
                  role="tab"
                  aria-selected={statusFilter === t.id}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="task-lists">
            {visibleLists.map((lst) => (
            <div key={lst.id} className="task-list-card">
              <div className="task-list-head">
                <h2>{lst.title}</h2>
                <div className="task-list-status-actions">
                  <span className="badge">
                    {STATUS_LABELS[lst.student_status] || lst.student_status}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline btn-xs"
                    disabled={finishingId === lst.id}
                    onClick={() => toggleFinish(lst)}
                    title={lst.student_status === 'DONE' ? 'Вернуть список в работу' : 'Сдать список преподавателю'}
                  >
                    {finishingId === lst.id
                      ? '...'
                      : (lst.student_status === 'DONE' ? 'Вернуть в работу' : 'Сдать список')}
                  </button>
                </div>
              </div>
              <div className="task-list-meta">
                {(() => {
                  const issuedStr = lst.issued_at ? new Date(lst.issued_at).toLocaleDateString('ru-RU') : '—';
                  const deadlineStr = lst.deadline_at ? new Date(lst.deadline_at).toLocaleDateString('ru-RU') : '—';
                  return (
                    <span className="task-list-date-range" aria-label="Сроки списка">
                      <span className="task-list-date" title="Выдано">{issuedStr}</span>
                      <span className="task-list-date-sep" aria-hidden> — </span>
                      <span className="task-list-date" title="Дедлайн">{deadlineStr}</span>
                    </span>
                  );
                })()}
              </div>
              {lst.student_finished_at && (
                <div className="task-list-finished-at text-secondary">
                  Сдано: {new Date(lst.student_finished_at).toLocaleString('ru-RU')}
                </div>
              )}
              <div className="task-list-body">
                {(!lst.items || lst.items.length === 0) ? (
                  <p className="text-secondary">Задачи ещё не добавлены.</p>
                ) : (
                  <div className="task-list-items-inline">
                    {lst.items.map((item) => {
                      const rep = item.latest_attempt || item.latest_self_report;
                      let mark = '·';
                      switch (rep?.self_report_status) {
                        case 'EASY':
                          mark = '++';
                          break;
                        case 'OK_NO_PEEK':
                          mark = '+';
                          break;
                        case 'OK_WITH_PEEK':
                          mark = '+-';
                          break;
                        case 'OK_WITH_QUESTION':
                          mark = '+?';
                          break;
                        case 'FAILED':
                          mark = '-';
                          break;
                        case 'NO_IDEAS':
                          mark = '--';
                          break;
                        case 'NOT_TRIED':
                          mark = '!';
                          break;
                        default:
                          mark = '·';
                      }
                      const statusClass =
                        rep?.self_report_status === 'EASY' ||
                        rep?.self_report_status === 'OK_NO_PEEK' ||
                        rep?.self_report_status === 'OK_WITH_PEEK' ||
                        rep?.self_report_status === 'OK_WITH_QUESTION'
                          ? 'mark-positive'
                          : rep?.self_report_status === 'FAILED' ||
                            rep?.self_report_status === 'NO_IDEAS'
                          ? 'mark-negative'
                          : 'mark-neutral';
                      return (
                        <Link
                          key={item.id}
                          to={`/work/${item.task.slug}?list=${lst.id}`}
                          className={`task-list-item-inline ${statusClass}`}
                        >
                          {taskNavRef(item.task)}{mark}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
